// ============================================================
// THE LADDER - what the opposition knows about the party.
//
// Four rungs: Unaware, Uneasy, Searching, Alarm. The GM moves it; the app
// only ever PROPOSES a step (`proposals.ts`). Each rung has a line the
// table reads ("A sentry has stopped and is looking toward the pipework"),
// built in until the GM writes their own.
//
// Beside the rung, each member of the opposition has a state of their own
// - unaware, investigating, searching, or away - which follows the rung
// until the GM says otherwise for that one. And there is a page of what
// the party has learned.
//
// All of it is one record on the phase frame (`PhaseFrame.stealth`): the
// GM's to write, read by everyone, and restored by undo with the frame.
//
// Pure. Zero React dependency.
// ============================================================

import { emptyApproach, readApproach } from './approach';
import type { Approach } from './approach';
import { readPublicContacts, type PublicContacts } from './contacts';

export const RUNGS = ['unaware', 'uneasy', 'searching', 'alarm'] as const;
export type Rung = (typeof RUNGS)[number];

export const RUNG_LABELS: Record<Rung, string> = {
  unaware: 'Unaware',
  uneasy: 'Uneasy',
  searching: 'Searching',
  alarm: 'Alarm',
};

/** What the table reads at each rung until the GM writes a line of their own. */
export const RUNG_LINES: Record<Rung, string> = {
  unaware: 'Nobody is looking your way.',
  uneasy: 'Something is not right. Someone has stopped to look.',
  searching: 'They know someone is here.',
  alarm: 'You are seen.',
};

export const OPP_STATES = ['unaware', 'investigating', 'searching', 'away'] as const;
export type OppState = (typeof OPP_STATES)[number];

const MAX_LINE = 200;
const MAX_LEARNED = 2000;

export interface StealthState {
  rung: Rung;
  /**
   * The GM's own line for EACH rung they have written one for. A rung keeps
   * its line: the ladder coming back to it finds it again (a line that was
   * dropped when the ladder moved read as a field that did nothing - user,
   * 2026-10-05).
   */
  lines: Partial<Record<Rung, string>>;
  /** The GM's line for the rung in force; empty = the built-in one. Always `lines[rung] ?? ''`. */
  line: string;
  /** The states the GM has set by hand, by combatant id. Anyone absent follows the rung. */
  opposition: Record<string, OppState>;
  /** What the party has learned, in the GM's words. */
  learned: string;
  /** The group approach: who leads, who helps, who is a burden (`approach.ts`). How well it went is not here. */
  approach: Approach;
  /**
   * What the players know of the figures they cannot see (engine/stealth/contacts), published by the GM's
   * screen; absent until there is something. Never names a figure.
   */
  contacts?: PublicContacts;
}

export const emptyStealth = (): StealthState => ({
  rung: 'unaware',
  line: '',
  lines: {},
  opposition: {},
  learned: '',
  approach: emptyApproach(),
});

const isRung = (value: unknown): value is Rung => RUNGS.includes(value as Rung);
const isOppState = (value: unknown): value is OppState => OPP_STATES.includes(value as OppState);
const text = (value: unknown, max: number): string => (typeof value === 'string' ? value.slice(0, max) : '');

function readOpposition(raw: unknown): Record<string, OppState> {
  if (!raw || typeof raw !== 'object') return {};
  return Object.fromEntries(Object.entries(raw).filter((entry): entry is [string, OppState] => isOppState(entry[1])));
}

/**
 * The stored lines, one per rung. A ladder saved before each rung kept its
 * own (v9.1.1 and earlier) holds one `line`: it was the line of the rung it
 * was saved at.
 */
function readLines(stored: Record<string, unknown>, rung: Rung): Partial<Record<Rung, string>> {
  const raw = (stored.lines && typeof stored.lines === 'object' ? stored.lines : {}) as Record<string, unknown>;
  const lines: Partial<Record<Rung, string>> = {};
  for (const r of RUNGS) {
    const line = text(raw[r], MAX_LINE);
    if (line.trim()) lines[r] = line;
  }
  const legacy = text(stored.line, MAX_LINE);
  if (!lines[rung] && legacy.trim()) lines[rung] = legacy;
  return lines;
}

/** A stored ladder, validated field by field. Anything it does not recognise reads as the default. */
export function readStealth(raw: unknown): StealthState {
  const stored = (raw ?? {}) as Record<string, unknown>;
  const rung = isRung(stored.rung) ? stored.rung : 'unaware';
  const lines = readLines(stored, rung);
  return {
    rung,
    line: lines[rung] ?? '',
    lines,
    opposition: readOpposition(stored.opposition),
    learned: text(stored.learned, MAX_LEARNED),
    approach: readApproach(stored.approach),
    ...(stored.contacts ? { contacts: readPublicContacts(stored.contacts) } : {}),
  };
}

/** The rung `by` steps from this one, never past either end. */
export function stepRung(rung: Rung, by: number): Rung {
  const at = Math.max(0, Math.min(RUNGS.length - 1, RUNGS.indexOf(rung) + Math.round(by)));
  return RUNGS[at];
}

/** The line the table reads: the GM's own, else the rung's. */
export const lineOf = (state: StealthState): string => state.line.trim() || RUNG_LINES[state.rung];

/**
 * Move the ladder. The line in force becomes the new rung's own (or none);
 * the old rung keeps its line for when the ladder comes back. The same rung
 * changes nothing.
 */
export function withRung(state: StealthState, rung: Rung): StealthState {
  return rung === state.rung ? state : { ...state, rung, line: state.lines[rung] ?? '' };
}

/** Write the line for the rung in force. A blank one takes it away: the built-in sentence is read again. */
export function withLine(state: StealthState, line: string): StealthState {
  const kept = line.slice(0, MAX_LINE);
  const { [state.rung]: _was, ...others } = state.lines;
  const lines = kept.trim() ? { ...others, [state.rung]: kept } : others;
  return { ...state, line: kept.trim() ? kept : '', lines };
}

export const withLearned = (state: StealthState, learned: string): StealthState => ({
  ...state,
  learned: learned.slice(0, MAX_LEARNED),
});

/** What somebody is doing when the GM has not said: they follow the rung. */
const BY_RUNG: Record<Rung, OppState> = {
  unaware: 'unaware',
  uneasy: 'unaware',
  searching: 'searching',
  alarm: 'searching',
};

/** This one's state: the GM's word for them, else the rung's. */
export const oppStateOf = (state: StealthState, id: string): OppState => state.opposition[id] ?? BY_RUNG[state.rung];

/**
 * Set one's state by hand - or, with `null`, hand them back to the rung.
 * Setting what the rung already says also hands them back: the record
 * holds only where somebody differs - except UNAWARE, which is kept
 * whatever the rung says (2026-10-21): the GM's "this one has not noticed"
 * must survive the rung rising, or a sentry set Unaware at Uneasy would be
 * searching the moment the ladder reached Searching.
 */
export function withOpposition(state: StealthState, id: string, to: OppState | null): StealthState {
  const { [id]: _was, ...rest } = state.opposition;
  const keep = to !== null && (to === 'unaware' || to !== BY_RUNG[state.rung]);
  return { ...state, opposition: keep ? { ...rest, [id]: to } : rest };
}

/**
 * An attack alerts whoever the GM had set Unaware (2026-10-21, the user's ruling): struck, they now search - as
 * the `Unw` condition is taken away by an attack (engine/unaware `alerted`). Set to searching, never handed back:
 * at Unaware or Uneasy the rung would call them unaware again. Whoever follows the ladder is the ladder's.
 * The same state when nobody changed.
 */
export function alertStruck(state: StealthState, struck: readonly string[]): StealthState {
  const alerted = struck.filter((id) => state.opposition[id] === 'unaware');
  if (!alerted.length) return state;
  // Stored even where the rung says the same: handed back at Searching, a rung stepped down would make them
  // unaware again without a word from the GM.
  const opposition = { ...state.opposition };
  for (const id of alerted) opposition[id] = 'searching';
  return { ...state, opposition };
}

/** Does the ladder know the party is here? From Searching on: nobody following it is unaware. */
export const rungKnows = (rung: Rung): boolean => rung === 'searching' || rung === 'alarm';

/**
 * The ladder with no hidden figure named in it (plan map-display, G1/H): the frame reaches every player, and a
 * watcher's state is keyed by its id. Every write the GM makes goes through this; a ladder that names none of
 * them comes back as it was.
 */
export function withoutHidden(state: StealthState, hidden: ReadonlySet<string>): StealthState {
  const named = Object.keys(state.opposition).filter((id) => hidden.has(id));
  if (!named.length) return state;
  const opposition = Object.fromEntries(Object.entries(state.opposition).filter(([id]) => !hidden.has(id)));
  return { ...state, opposition };
}

/** The word shown beside them: at Alarm whoever is present is simply hostile - but someone away, or set Unaware. */
export function oppLabel(state: StealthState, id: string): string {
  const own = oppStateOf(state, id);
  return state.rung === 'alarm' && own !== 'away' && own !== 'unaware' ? 'hostile' : own;
}

/**
 * Has this one not noticed the party? Whoever the GM has set Unaware, at
 * EVERY rung (2026-10-21: a Takedown was refused at Searching on a sentry
 * the panel showed unaware). Otherwise, through Unaware and Uneasy, anyone
 * who is not themselves investigating, searching or away; from Searching
 * on, nobody who follows the ladder: they know someone is here.
 */
export function isUnawareIn(state: StealthState, id: string): boolean {
  if (state.opposition[id] === 'unaware') return true;
  return !rungKnows(state.rung) && oppStateOf(state, id) === 'unaware';
}

/** Is this one looking for the party - the ones whose fall may let the scene settle? */
export function hasNoticed(state: StealthState, id: string): boolean {
  const own = oppStateOf(state, id);
  return own === 'investigating' || own === 'searching';
}
